import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { map, Observable } from 'rxjs';
import {
  AdminButtonComponent,
  AdminConfirmDialogService,
  AdminEmptyStateComponent,
  AdminIconComponent,
  AdminTextareaComponent,
} from '../../admin';
import { ApplicantNotesService } from '../../services/applicant-notes.service';
import {
  LeadDetailArea,
  LeadNotesService,
  PaginatedTimeline,
  TimelineItem,
} from '../../services/lead-notes.service';

export type TimelineEntityType = 'lead' | 'applicant';

export type LeadTimelineMode = 'all' | 'composer' | 'activity';

type LeadFieldChange = {
  field: string;
  oldValue: string | null;
  newValue: string | null;
};

const FIELD_LABELS: Record<string, string> = {
  fullName: 'Full name',
  leadSource: 'Lead source',
  email: 'Email',
  phoneNumber: 'Phone',
  whatsappNumber: 'WhatsApp',
  city: 'City',
  state: 'State',
  country: 'Country',
  qualification: 'Qualification',
  percentageOrCgpa: 'Percentage / CGPA',
  passingYear: 'Passing year',
  institution: 'Institution',
  preferredDestination: 'Preferred destination',
  preferredCourse: 'Preferred course',
  intake: 'Intake',
  budgetRange: 'Budget range',
  passportStatus: 'Passport status',
  englishTestStatus: 'English test',
  loanRequired: 'Loan required',
  stream: 'Stream',
  section: 'Section',
  message: 'Message',
};

@Component({
  selector: 'app-lead-timeline',
  standalone: true,
  imports: [
    DatePipe,
    FormsModule,
    AdminButtonComponent,
    AdminEmptyStateComponent,
    AdminIconComponent,
    AdminTextareaComponent,
  ],
  host: {
    class: 'lead-timeline-host',
  },
  templateUrl: './lead-timeline.component.html',
  styleUrl: './lead-timeline.component.scss',
})
export class LeadTimelineComponent {
  private readonly leadNotesService = inject(LeadNotesService);
  private readonly applicantNotesService = inject(ApplicantNotesService);
  private readonly confirmDialog = inject(AdminConfirmDialogService);

  readonly leadId = input.required<string>();
  readonly area = input.required<LeadDetailArea>();
  readonly entityType = input<TimelineEntityType>('lead');
  readonly currentUserId = input<string | null>(null);
  readonly isAdmin = input(false);
  readonly mode = input<LeadTimelineMode>('all');
  readonly includeArchivedNotes = input(false);

  readonly refreshed = output<void>();
  readonly noteRestored = output<void>();

  readonly items = signal<TimelineItem[]>([]);
  readonly loading = signal(false);
  readonly loadingMore = signal(false);
  readonly hasMore = signal(false);
  readonly page = signal(1);
  readonly noteSaving = signal(false);
  readonly noteError = signal<string | null>(null);

  newNoteText = '';
  readonly editingNoteId = signal<string | null>(null);
  editNoteText = '';

  private readonly includeArchivedReload = effect(() => {
    const id = this.leadId();
    this.includeArchivedNotes();
    this.mode();
    if (!id || !this.showActivity()) return;
    untracked(() => this.loadTimeline(true));
  });

  showComposer(): boolean {
    const mode = this.mode();
    return mode === 'all' || mode === 'composer';
  }

  showActivity(): boolean {
    const mode = this.mode();
    return mode === 'all' || mode === 'activity';
  }

  reload(): void {
    if (this.showActivity()) {
      this.loadTimeline(true);
    }
  }

  loadTimeline(reset: boolean): void {
    const id = this.leadId();
    if (!id) return;

    if (reset) {
      this.page.set(1);
      this.loading.set(true);
    } else {
      this.loadingMore.set(true);
    }

    this.fetchTimeline(this.area(), id, this.page(), 20).subscribe({
      next: (data) => {
        this.items.set(reset ? data.items : [...this.items(), ...data.items]);
        this.hasMore.set(data.hasMore);
        this.loading.set(false);
        this.loadingMore.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.loadingMore.set(false);
      },
    });
  }

  loadMore(): void {
    this.page.update((p) => p + 1);
    this.loadTimeline(false);
  }

  submitNote(): void {
    const text = this.newNoteText.trim();
    const id = this.leadId();
    if (!text || !id) return;

    this.noteSaving.set(true);
    this.noteError.set(null);
    this.createNoteRequest(this.area(), id, text).subscribe({
      next: () => {
        this.newNoteText = '';
        this.noteSaving.set(false);
        this.refreshed.emit();
      },
      error: (err: HttpErrorResponse) => {
        this.noteSaving.set(false);
        this.noteError.set(err?.error?.message ?? 'Failed to add note.');
      },
    });
  }

  canModifyNote(item: TimelineItem): boolean {
    if (this.isArchivedNote(item)) return false;
    if (item.payload.referenceType !== 'NOTE' || !item.payload.referenceId) return false;
    const uid = this.currentUserId();
    if (!uid) return false;
    return this.isAdmin() || item.createdBy.userId === uid;
  }

  canRestoreNote(item: TimelineItem): boolean {
    if (!this.isArchivedNote(item)) return false;
    if (item.payload.referenceType !== 'NOTE' || !item.payload.referenceId) return false;
    const uid = this.currentUserId();
    if (!uid) return false;
    return this.isAdmin() || item.createdBy.userId === uid;
  }

  isArchivedNote(item: TimelineItem): boolean {
    return item.payload.metadata?.['isArchived'] === true;
  }

  isNoteItem(item: TimelineItem): boolean {
    return item.type === 'NOTE_ADDED' || item.type === 'NOTE_UPDATED';
  }

  eventTitle(item: TimelineItem): string {
    switch (item.type) {
      case 'WORKFLOW_TRANSITION': {
        const toStatus = item.payload.metadata?.['toStatus'] as string | undefined;
        if (toStatus) {
          return `Status Changed to ${this.titleCaseStatus(toStatus)}`;
        }
        return 'Status Changed';
      }
      case 'NOTE_ADDED':
        return this.isArchivedNote(item) ? 'Deleted Note' : 'New Note Added';
      case 'NOTE_UPDATED':
        return this.isArchivedNote(item) ? 'Deleted Note' : 'Note Updated';
      case 'NOTE_DELETED':
        return 'Note Deleted';
      case 'LEAD_CREATED':
        return 'Lead Registered';
      case 'LEAD_UPDATED':
        return 'Lead Updated';
      case 'LEAD_CONVERTED':
        return 'Lead Enrolled as Applicant';
      case 'LEAD_MERGED':
        return 'Leads Merged';
      case 'SUB_STATUS_CHANGED':
        return 'Sub Status Changed';
      case 'APPLICANT_CREATED':
        return 'Applicant Created';
      case 'APPLICANT_UPDATED':
        return 'Applicant Updated';
      case 'OWNER_ASSIGNED':
        return 'Owner Assigned';
      case 'OWNER_CHANGED':
        return 'Owner Changed';
      case 'TASK_CREATED':
        return 'Task Created';
      case 'TASK_UPDATED':
        return 'Task Updated';
      case 'TASK_STATUS_CHANGED':
        return 'Task Status Changed';
      case 'TASK_DELETED':
        return 'Task Deleted';
      case 'TASK_REMINDER_SCHEDULED':
        return 'Task Reminder Scheduled';
      case 'TASK_REMINDER_RESCHEDULED':
        return 'Task Reminder Rescheduled';
      case 'TASK_REMINDER_CANCELLED':
        return 'Task Reminder Cancelled';
      case 'TASK_REMINDER_SENT':
        return 'Task Reminder Sent';
      case 'TASK_REMINDER_FAILED':
        return 'Task Reminder Failed';
      case 'EMAIL_SENT':
        return 'Email Sent';
      default:
        return item.payload.title || item.type;
    }
  }

  emailSubject(item: TimelineItem): string | null {
    const subject = item.payload.metadata?.['subject'];
    return typeof subject === 'string' && subject.trim() ? subject.trim() : null;
  }

  emailRecipientsText(item: TimelineItem, key: 'to' | 'cc' | 'bcc'): string | null {
    const value = item.payload.metadata?.[key];
    if (!Array.isArray(value) || value.length === 0) return null;
    const emails = value
      .filter((entry): entry is string => typeof entry === 'string' && !!entry.trim())
      .map((entry) => entry.trim());
    return emails.length ? emails.join(', ') : null;
  }

  emailPreview(item: TimelineItem): string | null {
    const preview = item.payload.metadata?.['preview'];
    if (typeof preview === 'string' && preview.trim()) return preview.trim();
    return item.payload.description?.trim() || null;
  }

  ownershipChangeText(item: TimelineItem): string | null {
    const meta = item.payload.metadata ?? {};
    const previous =
      (meta['previousOwnerDisplayName'] as string | undefined) ||
      (meta['previousOwnerUserId'] as string | undefined) ||
      'Unassigned';
    const next =
      (meta['newOwnerDisplayName'] as string | undefined) ||
      (meta['newOwnerUserId'] as string | undefined);
    if (!next) return null;
    if (item.type === 'OWNER_ASSIGNED') {
      return next;
    }
    return `${previous} → ${next}`;
  }

  isOwnershipItem(item: TimelineItem): boolean {
    return item.type === 'OWNER_ASSIGNED' || item.type === 'OWNER_CHANGED';
  }

  eventAttribution(item: TimelineItem): string {
    const name = item.createdBy.displayName;
    if (this.isNoteItem(item)) {
      return `— Added by ${name}`;
    }
    return `— By ${name}`;
  }

  startEditNote(item: TimelineItem): void {
    const noteId = item.payload.referenceId;
    if (!noteId) return;
    this.editingNoteId.set(noteId);
    this.editNoteText = this.noteText(item);
  }

  cancelEditNote(): void {
    this.editingNoteId.set(null);
    this.editNoteText = '';
  }

  saveEditNote(item: TimelineItem): void {
    const noteId = item.payload.referenceId;
    const id = this.leadId();
    const text = this.editNoteText.trim();
    if (!noteId || !id || !text) return;

    this.noteSaving.set(true);
    this.updateNoteRequest(this.area(), id, noteId, text).subscribe({
      next: () => {
        this.noteSaving.set(false);
        this.cancelEditNote();
        this.loadTimeline(true);
        this.refreshed.emit();
      },
      error: (err: HttpErrorResponse) => {
        this.noteSaving.set(false);
        this.noteError.set(err?.error?.message ?? 'Failed to update note.');
      },
    });
  }

  async deleteNote(item: TimelineItem): Promise<void> {
    const noteId = item.payload.referenceId;
    const id = this.leadId();
    if (!noteId || !id) return;

    const confirmed = await this.confirmDialog.confirm({
      title: 'Delete note',
      message: 'Delete this note? It will be hidden from the activity timeline.',
      confirmLabel: 'Delete',
      confirmVariant: 'danger',
    });
    if (!confirmed) return;

    this.noteSaving.set(true);
    this.deleteNoteRequest(this.area(), id, noteId).subscribe({
      next: () => {
        this.noteSaving.set(false);
        this.loadTimeline(true);
        this.refreshed.emit();
      },
      error: (err: HttpErrorResponse) => {
        this.noteSaving.set(false);
        this.noteError.set(err?.error?.message ?? 'Failed to delete note.');
      },
    });
  }

  async restoreNote(item: TimelineItem): Promise<void> {
    const noteId = item.payload.referenceId;
    const id = this.leadId();
    if (!noteId || !id) return;

    this.noteSaving.set(true);
    this.noteError.set(null);
    this.restoreNoteRequest(this.area(), id, noteId).subscribe({
      next: () => {
        this.noteSaving.set(false);
        this.noteRestored.emit();
        this.refreshed.emit();
      },
      error: (err: HttpErrorResponse) => {
        this.noteSaving.set(false);
        this.noteError.set(err?.error?.message ?? 'Failed to restore note.');
      },
    });
  }

  workflowDescription(item: TimelineItem): string | null {
    const comment = this.workflowComment(item);
    if (comment) return comment;
    if (item.payload.description?.trim()) return item.payload.description.trim();
    return this.workflowStatusChangeText(item);
  }

  workflowStatusChangeText(item: TimelineItem): string | null {
    const meta = item.payload.metadata ?? {};
    const fromStatus = meta['fromStatus'] as string | undefined;
    const toStatus = meta['toStatus'] as string | undefined;
    if (fromStatus && toStatus) {
      return `from "${fromStatus}" → "${toStatus}"`;
    }
    return null;
  }

  workflowSubStatusClearedText(item: TimelineItem): string | null {
    if (item.type !== 'WORKFLOW_TRANSITION') return null;
    const meta = item.payload.metadata ?? {};
    if (!meta['subStatusCleared']) return null;
    const fromSubStatus = meta['fromSubStatus'] as string | undefined;
    if (fromSubStatus?.trim()) {
      return `Sub Status cleared (was "${fromSubStatus.trim()}")`;
    }
    return 'Sub Status cleared';
  }

  subStatusChangeText(item: TimelineItem): string | null {
    if (item.type !== 'SUB_STATUS_CHANGED') return null;
    const meta = item.payload.metadata ?? {};
    const fromSubStatus = meta['fromSubStatus'] as string | null | undefined;
    const toSubStatus = meta['toSubStatus'] as string | null | undefined;
    const fromLabel = fromSubStatus?.trim() ? fromSubStatus.trim() : 'None';
    const toLabel = toSubStatus?.trim() ? toSubStatus.trim() : 'None';
    return `from "${fromLabel}" → "${toLabel}"`;
  }

  workflowComment(item: TimelineItem): string | null {
    const comment = item.payload.metadata?.['comment'];
    if (typeof comment === 'string' && comment.trim()) return comment.trim();
    return null;
  }

  noteText(item: TimelineItem): string {
    const note = item.payload.metadata?.['note'];
    return typeof note === 'string' ? note : '';
  }

  leadChanges(item: TimelineItem): LeadFieldChange[] {
    const raw = item.payload.metadata?.['changes'];
    if (!Array.isArray(raw)) return [];
    return raw as LeadFieldChange[];
  }

  fieldLabel(field: string): string {
    return FIELD_LABELS[field] ?? field;
  }

  formatValue(value: string | null | undefined, isNew = false): string {
    if (value === null || value === undefined || value === '') {
      return isNew ? 'cleared' : '—';
    }
    return value;
  }

  private fetchTimeline(
    area: LeadDetailArea,
    id: string,
    page: number,
    limit: number,
  ): Observable<PaginatedTimeline> {
    const includeArchivedNotes = this.includeArchivedNotes();
    if (this.entityType() === 'applicant') {
      return this.applicantNotesService.getTimeline(area, id, page, limit, includeArchivedNotes);
    }
    return this.leadNotesService.getTimeline(area, id, page, limit, includeArchivedNotes);
  }

  private createNoteRequest(area: LeadDetailArea, id: string, note: string): Observable<void> {
    if (this.entityType() === 'applicant') {
      return this.applicantNotesService.createNote(area, id, note).pipe(map(() => undefined));
    }
    return this.leadNotesService.createNote(area, id, note).pipe(map(() => undefined));
  }

  private updateNoteRequest(
    area: LeadDetailArea,
    id: string,
    noteId: string,
    note: string,
  ): Observable<void> {
    if (this.entityType() === 'applicant') {
      return this.applicantNotesService
        .updateNote(area, id, noteId, note)
        .pipe(map(() => undefined));
    }
    return this.leadNotesService.updateNote(area, id, noteId, note).pipe(map(() => undefined));
  }

  private deleteNoteRequest(
    area: LeadDetailArea,
    id: string,
    noteId: string,
  ): Observable<{ deleted: boolean }> {
    if (this.entityType() === 'applicant') {
      return this.applicantNotesService.deleteNote(area, id, noteId);
    }
    return this.leadNotesService.deleteNote(area, id, noteId);
  }

  private restoreNoteRequest(
    area: LeadDetailArea,
    id: string,
    noteId: string,
  ): Observable<unknown> {
    if (this.entityType() === 'applicant') {
      return this.applicantNotesService.restoreNote(area, id, noteId);
    }
    return this.leadNotesService.restoreNote(area, id, noteId);
  }

  private titleCaseStatus(code: string): string {
    return code
      .toLowerCase()
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  }
}
